import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminOpsProvider } from './contexts/AdminOpsContext';
import { AppShell } from './components/AppShell';
import './i18n';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import PostCreator from './pages/PostCreator';
import ContentStudio from './pages/ContentStudio';
import AIStudio from './pages/AIStudio';
import CalendarPage from './pages/CalendarPage';
import ActivityPage from './pages/ActivityPage';
import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import AdminPage from './pages/AdminPage';
import PitchPage from './pages/PitchPage';

// Auto-login component - passwordless admin access (DEV ONLY)
function AutoLogin() {
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const redirect = params.get('redirect') || '/admin';
    
    if (token) {
      localStorage.setItem('tekcep_token', token);
      window.location.href = redirect;
    } else {
      // Fetch admin token from bypass endpoint
      const API = process.env.REACT_APP_BACKEND_URL || '';
      fetch(`${API}/api/auth/admin-bypass`)
        .then(r => r.json())
        .then(data => {
          if (data.token) {
            localStorage.setItem('tekcep_token', data.token);
            window.location.href = redirect;
          }
        })
        .catch(() => { window.location.href = '/auth'; });
    }
  }, []);
  
  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--primary))]"></div>
    </div>
  );
}

import { OnboardingScreen } from './components/Onboarding';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--primary))]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Show onboarding for new users
  if (user.onboarding_completed === false) {
    return <OnboardingScreen />;
  }
  
  return <AppShell>{children}</AppShell>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--primary))]"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/auth" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  
  return <AppShell>{children}</AppShell>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--primary))]"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pitch" element={<PitchPage />} />
      <Route path="/auto-login" element={<AutoLogin />} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/create" element={<ProtectedRoute><PostCreator /></ProtectedRoute>} />
      <Route path="/studio" element={<ProtectedRoute><ContentStudio /></ProtectedRoute>} />
      <Route path="/ai-studio" element={<ProtectedRoute><AIStudio /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/activity" element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminOpsProvider>
          <AppRoutes />
          <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: 'hsl(220, 12%, 8%)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'hsl(210, 40%, 98%)',
            }
          }}
        />
        </AdminOpsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
